import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { BrandLogo } from "@/components/BrandLogo";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";
import { PasswordInput } from "@/components/PasswordInput";
import { ApiPaths, api } from "@/lib/api";

/** Reset-password — reads the `?token=` query param, captures a
 * new password (twice for confirmation), fires
 * POST /v1/auth/password-reset/confirm. On 200, redirects to /login.
 * On 410 (token expired / used / unknown) we tell the user to
 * request a fresh reset; on 400 (weak password) we re-render the
 * form with the validator errors. */
type FormValues = { newPassword: string; confirmPassword: string };

interface ApiErrorBody {
  detail?: string;
  errors?: string[];
}

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [token, setToken] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [serverFieldErrors, setServerFieldErrors] = useState<string[]>([]);
  const [tokenGone, setTokenGone] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState,
  } = useForm<FormValues>({
    defaultValues: { newPassword: "", confirmPassword: "" },
  });

  useEffect(() => {
    // Read the token from the URL once at mount. Strip it from the
    // address bar afterwards so it doesn't leak into browser history
    // or get accidentally shared via a "copy URL" action.
    const params = new URLSearchParams(window.location.search);
    const t0 = params.get("token");
    if (t0) {
      setToken(t0);
      const cleaned = window.location.pathname;
      window.history.replaceState({}, "", cleaned);
    }
  }, []);

  const newPassword = watch("newPassword");

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    setServerFieldErrors([]);
    if (!token) {
      setServerError(t("auth.resetTokenMissing"));
      return;
    }
    if (values.newPassword !== values.confirmPassword) {
      setServerError(t("auth.resetPasswordMismatch"));
      return;
    }
    try {
      await api.post(ApiPaths.authPasswordResetConfirm, {
        token,
        new_password: values.newPassword,
      });
      // Success — bounce to /login.
      window.location.assign("/login?reset=ok");
    } catch (e: unknown) {
      // The api client throws a structured error; pull status + body
      // out of it. Fallback to the string message if not present.
      type StructuredError = {
        status?: number;
        body?: ApiErrorBody;
        message?: string;
      };
      const err = e as StructuredError;
      const status = err.status;
      const body = err.body;
      if (status === 410) {
        setTokenGone(true);
        return;
      }
      if (status === 400 && body?.errors?.length) {
        setServerFieldErrors(body.errors);
        return;
      }
      setServerError(
        body?.detail || err.message || t("auth.resetConfirmError")
      );
    }
  };

  return (
    <main className="min-h-dvh bg-nb-bg py-10">
      <div className="mx-auto max-w-md px-6">
        <div className="mb-6 flex justify-center">
          <BrandLogo />
        </div>
        <div className="rounded-xl border border-nb-border bg-nb-surface/30 p-8">
          <div className="flex items-start justify-between gap-3">
            <h1 className="font-display text-2xl font-semibold text-nb-text">
              {t("auth.resetPasswordTitle")}
            </h1>
            <LocaleSwitcher />
          </div>

          {tokenGone ? (
            <div className="mt-6 space-y-4">
              <div className="rounded-md border border-nb-danger/40 bg-nb-danger/5 p-4 text-sm text-nb-text">
                {t("auth.resetTokenGone")}
              </div>
              <a
                className="btn-primary w-full"
                href="/forgot-password"
              >
                {t("auth.resetRequestAgain")}
              </a>
            </div>
          ) : !token ? (
            <div className="mt-6 rounded-md border border-nb-danger/40 bg-nb-danger/5 p-4 text-sm text-nb-text">
              {t("auth.resetTokenMissing")}{" "}
              <a className="text-nb-mint hover:underline" href="/forgot-password">
                {t("auth.resetRequestAgain")}
              </a>
            </div>
          ) : (
            <form className="mt-6 space-y-5" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label className="label" htmlFor="newPassword">
                  {t("auth.newPassword")}
                </label>
                <PasswordInput
                  id="newPassword"
                  autoComplete="new-password"
                  autoFocus
                  {...register("newPassword", {
                    required: true,
                    minLength: 12,
                  })}
                />
                <p className="mt-1 text-xs text-nb-text-3">
                  {t("auth.passwordMinLengthHint")}
                </p>
              </div>

              <div>
                <label className="label" htmlFor="confirmPassword">
                  {t("auth.confirmNewPassword")}
                </label>
                <PasswordInput
                  id="confirmPassword"
                  autoComplete="new-password"
                  {...register("confirmPassword", {
                    required: true,
                    minLength: 12,
                    validate: (v) =>
                      v === newPassword || t("auth.resetPasswordMismatch"),
                  })}
                />
              </div>

              {serverFieldErrors.length > 0 && (
                <ul className="space-y-1 text-sm text-nb-danger">
                  {serverFieldErrors.map((m, i) => (
                    <li key={i}>· {m}</li>
                  ))}
                </ul>
              )}

              {serverError && (
                <p role="alert" className="text-sm text-nb-danger">
                  {serverError}
                </p>
              )}

              <button
                type="submit"
                className="btn-primary w-full"
                disabled={formState.isSubmitting}
              >
                {formState.isSubmitting
                  ? t("common.loading")
                  : t("auth.resetConfirmSubmit")}
              </button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
